import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { admin } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Users, Shield, Ban, Trash2, RefreshCw, UserCog,
  ChevronLeft, ChevronRight, Eye, Crown, Clock,
} from 'lucide-react';
import { USER_ROLES } from '../constants/theme';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function UsersPage() {
  const { user: currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogType, setDialogType] = useState(null); // 'role' | 'ban' | 'delete' | 'arcanien'
  const [newRole, setNewRole] = useState('');
  const [arcanienForm, setArcanienForm] = useState({ duration: '3', message: '' });
  const limit = 10;

  useEffect(() => {
    loadUsers();
  }, [page, roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      
      const response = await admin.getUsers(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadUsers();
  };

  const openDialog = (user, type) => {
    setSelectedUser(user);
    setDialogType(type);
    if (type === 'role') setNewRole(user.role);
  };

  const closeDialog = () => {
    setSelectedUser(null);
    setDialogType(null);
    setNewRole('');
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await admin.updateUserRole(selectedUser.id, newRole);
      toast.success('Rôle mis à jour');
      closeDialog();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      if (selectedUser.is_banned) {
        await admin.unbanUser(selectedUser.id);
        toast.success('Utilisateur débanni');
      } else {
        await admin.banUser(selectedUser.id, 'Banni par admin');
        toast.success('Utilisateur banni');
      }
      closeDialog();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du bannissement');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await admin.deleteUser(selectedUser.id);
      toast.success('Utilisateur supprimé');
      closeDialog();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await admin.resetPassword(userId);
      toast.success(`Mot de passe réinitialisé: ${response.data.new_password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const handleToggleArcanien = async (targetUser) => {
    if (targetUser.is_arcanien) {
      // Remove directly
      try {
        await admin.toggleArcanien(targetUser.id, { is_arcanien: false });
        toast.success('Arcanien retiré');
        loadUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
    } else {
      // Open modal to set duration + message
      setSelectedUser(targetUser);
      setArcanienForm({ duration: '3', message: '' });
      setDialogType('arcanien');
    }
  };

  const handleGrantArcanien = async () => {
    if (!selectedUser) return;
    try {
      await admin.toggleArcanien(selectedUser.id, {
        is_arcanien: true,
        duration_months: parseInt(arcanienForm.duration) || 0,
        message: arcanienForm.message,
      });
      toast.success(`Arcanien accordé à ${selectedUser.pseudo || selectedUser.name} pour ${arcanienForm.duration} mois`);
      setDialogType(null);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const getRoleBadgeColor = (role) => {
    const colors = {
      ADMIN: 'bg-red-500/20 text-red-400 border-red-500/30',
      EDITOR: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      ORG: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      ARCANIEN: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
      PLAYER: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      VISITOR: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return colors[role] || colors.VISITOR;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Utilisateurs</h1>
          <p className="text-[#666] mt-1">{total} utilisateurs au total</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <Input
                placeholder="Rechercher par nom, email ou pseudo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#252525] border-[#333] text-white placeholder:text-[#666]"
                data-testid="users-search-input"
              />
            </div>
            <Select value={roleFilter || "all"} onValueChange={(v) => { setRoleFilter(v === "all" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-full md:w-48 bg-[#252525] border-[#333] text-white">
                <SelectValue placeholder="Tous les rôles" />
              </SelectTrigger>
              <SelectContent className="bg-[#252525] border-[#333]">
                <SelectItem value="all" className="text-white">Tous les rôles</SelectItem>
                {Object.entries(USER_ROLES).map(([key, label]) => (
                  <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#FF6B35] hover:bg-[#E55A25]">
              Rechercher
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left p-4 text-[#666] font-medium">Utilisateur</th>
                    <th className="text-left p-4 text-[#666] font-medium">Email</th>
                    <th className="text-left p-4 text-[#666] font-medium">Rôle</th>
                    <th className="text-left p-4 text-[#666] font-medium">Arcanien</th>
                    <th className="text-left p-4 text-[#666] font-medium">Statut</th>
                    <th className="text-right p-4 text-[#666] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-[#333] hover:bg-[#252525]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#FF6B35]/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#FF6B35]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            {user.pseudo && (
                              <p className="text-[#666] text-sm">@{user.pseudo}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-[#B0B0B0]">{user.email}</td>
                      <td className="p-4">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {USER_ROLES[user.role] || user.role}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {user.is_arcanien ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Crown className="w-3 h-3 mr-1" />Oui</Badge>
                        ) : (
                          <span className="text-[#666]">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.is_banned ? (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            Banni
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            Actif
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10"
                            title="Voir le profil"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDialog(user, 'role')}
                              className="text-[#666] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10"
                              title="Modifier le rôle"
                              data-testid={`edit-role-${user.id}`}
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleArcanien(user)}
                              className={user.is_arcanien
                                ? "text-yellow-400 hover:text-yellow-500 hover:bg-yellow-500/10"
                                : "text-[#666] hover:text-yellow-400 hover:bg-yellow-400/10"
                              }
                              title={user.is_arcanien ? 'Retirer Arcanien' : 'Donner Arcanien'}
                              data-testid={`toggle-arcanien-${user.id}`}
                            >
                              <Crown className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleResetPassword(user.id)}
                              className="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10"
                              title="Réinitialiser mot de passe"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDialog(user, 'ban')}
                            className={user.is_banned 
                              ? "text-green-400 hover:text-green-500 hover:bg-green-500/10"
                              : "text-[#666] hover:text-yellow-400 hover:bg-yellow-400/10"
                            }
                            title={user.is_banned ? 'Débannir' : 'Bannir'}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                          {isAdmin && user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDialog(user, 'delete')}
                              className="text-[#666] hover:text-red-400 hover:bg-red-400/10"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#333]">
            <p className="text-[#666] text-sm">
              Page {page + 1} sur {totalPages || 1}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-[#333] text-white hover:bg-[#333]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="border-[#333] text-white hover:bg-[#333]"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={dialogType === 'role'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
            <DialogDescription className="text-[#666]">
              Modifier le rôle de {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              {Object.entries(USER_ROLES).map(([key, label]) => (
                <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button onClick={handleUpdateRole} className="bg-[#FF6B35] hover:bg-[#E55A25]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={dialogType === 'ban'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.is_banned ? 'Débannir' : 'Bannir'} l'utilisateur
            </DialogTitle>
            <DialogDescription className="text-[#666]">
              {selectedUser?.is_banned 
                ? `Êtes-vous sûr de vouloir débannir ${selectedUser?.name} ?`
                : `Êtes-vous sûr de vouloir bannir ${selectedUser?.name} ?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button 
              onClick={handleBanUser} 
              className={selectedUser?.is_banned 
                ? "bg-green-500 hover:bg-green-600"
                : "bg-yellow-500 hover:bg-yellow-600 text-black"
              }
            >
              {selectedUser?.is_banned ? 'Débannir' : 'Bannir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogType === 'delete'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Supprimer l'utilisateur</DialogTitle>
            <DialogDescription className="text-[#666]">
              Êtes-vous sûr de vouloir supprimer {selectedUser?.name} ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button onClick={handleDeleteUser} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arcanien Dialog */}
      <Dialog open={dialogType === 'arcanien'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" /> Accorder le statut Arcanien
            </DialogTitle>
            <DialogDescription className="text-[#666]">
              Accorder le statut Arcanien à <strong className="text-white">{selectedUser?.pseudo || selectedUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FF6B35]" /> Durée (en mois)
              </label>
              <Select value={arcanienForm.duration} onValueChange={(v) => setArcanienForm(f => ({ ...f, duration: v }))}>
                <SelectTrigger className="bg-[#252525] border-[#333] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#252525] border-[#333]">
                  <SelectItem value="1" className="text-white">1 mois</SelectItem>
                  <SelectItem value="3" className="text-white">3 mois</SelectItem>
                  <SelectItem value="6" className="text-white">6 mois</SelectItem>
                  <SelectItem value="12" className="text-white">12 mois</SelectItem>
                  <SelectItem value="0" className="text-white">Illimité</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message pour le joueur</label>
              <textarea
                className="w-full h-24 rounded-md bg-[#252525] border border-[#333] text-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                placeholder="Message qui apparaitra dans un popup à l'ouverture de l'app..."
                value={arcanienForm.message}
                onChange={(e) => setArcanienForm(f => ({ ...f, message: e.target.value }))}
                data-testid="arcanien-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button onClick={handleGrantArcanien} className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold" data-testid="grant-arcanien-btn">
              <Crown className="w-4 h-4 mr-2" /> Accorder Arcanien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
